import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCcw, Mail, Phone, MessageCircle, LogOut, Search, Filter,
  Trash2, Archive, ShieldOff, X, Download, Tag, Circle, Clock, User, Send,
  ChevronDown, KeyRound, Kanban as KanbanIcon, Video as VideoIcon, Image as ImageIcon, Images as ImagesIcon, Settings as SettingsIcon, Users as UsersIcon, CreditCard as CreditCardIcon,
} from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  neu: "Neu", unbearbeitet: "Unbearbeitet", kontaktaufnahme: "Kontaktaufnahme",
  kontaktiert: "Kontaktiert", qualifiziert: "Qualifiziert",
  termin_vereinbart: "Termin vereinbart", beratung_durchgefuehrt: "Beratung durchgeführt",
  angebot_versendet: "Angebot versendet", nachfassen: "Nachfassen",
  gewonnen: "Gewonnen", verloren: "Verloren", nicht_erreichbar: "Nicht erreichbar",
  ungeeignet: "Ungeeignet", spam: "Spam", test: "Test",
};

const STATUS_COLORS = {
  neu: "#E8E5D0", unbearbeitet: "#F4C572", kontaktaufnahme: "#F4C572",
  kontaktiert: "#B8CFA0", qualifiziert: "#B8CFA0", termin_vereinbart: "#B8CFA0",
  beratung_durchgefuehrt: "#A0C87B", angebot_versendet: "#A0C87B",
  nachfassen: "#F4C572", gewonnen: "#7FB960", verloren: "#E27D7D",
  nicht_erreichbar: "#B39C6A", ungeeignet: "#B39C6A", spam: "#B39C6A", test: "#8F8C7A",
};

const AdminLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [archivedFilter, setArchivedFilter] = useState("active"); // active|archived|all
  const [selected, setSelected] = useState(new Set());
  const [detailLead, setDetailLead] = useState(null);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [me, setMe] = useState(null);

  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(() => axios.create({
    baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 15000,
  }), [token]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); navigate("/admin/login", { replace: true }); };

  const fetchLeads = async () => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    setLoading(true); setErr(null);
    try {
      const res = await api.get("/leads?limit=500");
      setLeads(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403 && detail === "PASSWORD_CHANGE_REQUIRED") { setShowPwdModal(true); setLoading(false); return; }
      if ([401, 403].includes(status)) { localStorage.removeItem(TOKEN_KEY); navigate("/admin/login", { replace: true }); return; }
      setErr("Fehler beim Laden.");
    } finally { setLoading(false); }
  };

  const fetchMe = async () => {
    if (!token) return;
    try {
      const r = await api.get("/admin/me");
      setMe(r.data);
      if (r.data.must_change_password) setShowPwdModal(true);
    } catch (e) { /* auth errors handled by fetchLeads */ }
  };

  useEffect(() => { document.title = "CRM · Leads"; fetchLeads(); fetchMe(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter(l => {
      if (archivedFilter === "active" && l.archived) return false;
      if (archivedFilter === "archived" && !l.archived) return false;
      if (statusFilter && l.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${l.first_name} ${l.last_name} ${l.email} ${l.mobile} ${l.city} ${l.message || ""} ${(l.tags||[]).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [leads, search, statusFilter, archivedFilter]);

  const toggle = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const toggleAll = () => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map(l => l.id)));

  const bulk = async (action, value = null) => {
    if (selected.size === 0) { toast.error("Keine Leads ausgewählt."); return; }
    const ids = Array.from(selected);
    if (action === "delete") {
      if (!window.confirm(`Wirklich ${ids.length} Lead(s) dauerhaft löschen?`)) return;
      if (ids.length > 5 && !window.confirm(`Sicherheitsabfrage: ${ids.length} Leads werden endgültig gelöscht. Fortfahren?`)) return;
    }
    try {
      const res = await api.post("/leads/bulk", { ids, action, value });
      toast.success(`${res.data.affected} Lead(s) verarbeitet.`);
      setSelected(new Set()); fetchLeads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen."); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `leads-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("CSV heruntergeladen.");
    } catch (e) { toast.error("Export fehlgeschlagen."); }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-leads-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-2">
              <ArrowLeft size={12} /> Zur Website
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">CRM · Leads</h1>
            <p className="text-[#A8A58F] text-sm mt-1">{filtered.length} von {leads.length} · {selected.size} ausgewählt</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/admin/pipeline" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm">
              <KanbanIcon size={14} /> Pipeline
            </Link>
            <Link to="/admin/images" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-nav-images">
              <ImageIcon size={14} /> Bilder
            </Link>
            <Link to="/admin/site-images" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-nav-site-images">
              <ImagesIcon size={14} /> Website-Bilder
            </Link>
            <Link to="/admin/transformations" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-nav-transformations">
              <ImagesIcon size={14} /> Vorher / Nachher
            </Link>
            <Link to="/admin/purchase-links" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-nav-purchase-links">
              <CreditCardIcon size={14} /> Kauf-Links
            </Link>
            <Link to="/admin/videos" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-nav-videos">
              <VideoIcon size={14} /> Videos
            </Link>
            <Link to="/admin/settings" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-nav-settings">
              <SettingsIcon size={14} /> Tracking
            </Link>
            <Link to="/admin/users" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-nav-users">
              <UsersIcon size={14} /> Admins
            </Link>
            <button onClick={() => setShowPwdModal(true)} className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-password-btn">
              <KeyRound size={14} /> Passwort
            </button>
            <button onClick={fetchLeads} className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-refresh-btn">
              <RefreshCcw size={14} /> Neu laden
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="glass-strong rounded-2xl p-4 mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A58F]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen: Name, E-Mail, Nachricht, Tags…"
              className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg pl-9 pr-3 py-2 text-sm text-[#F3F1E8] placeholder:text-[#A8A58F] outline-none focus:border-[#E8E5D0]"
              data-testid="admin-search" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] outline-none"
            data-testid="admin-status-filter">
            <option value="">Alle Status</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={archivedFilter} onChange={e => setArchivedFilter(e.target.value)}
            className="bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] outline-none">
            <option value="active">Aktiv</option>
            <option value="archived">Archiviert</option>
            <option value="all">Alle</option>
          </select>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-bold rounded-lg px-3 py-2 text-sm" data-testid="admin-export-csv">
            <Download size={14} /> CSV
          </button>
        </div>

        {/* Bulk Bar */}
        {selected.size > 0 && (
          <div className="glass-strong rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="bulk-bar">
            <span className="text-sm text-[#F3F1E8] font-bold mr-2">{selected.size} ausgewählt:</span>
            <select onChange={e => e.target.value && bulk("set_status", e.target.value)} defaultValue=""
              className="bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-2 py-1.5 text-xs text-[#F3F1E8]">
              <option value="">Status setzen…</option>
              {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button onClick={() => bulk("archive")} className="inline-flex items-center gap-1 border border-white/15 hover:bg-black/[0.15] text-xs font-bold rounded-lg px-3 py-1.5" data-testid="bulk-archive"><Archive size={12}/> Archivieren</button>
            <button onClick={() => bulk("anonymize")} className="inline-flex items-center gap-1 border border-white/15 hover:bg-black/[0.15] text-xs font-bold rounded-lg px-3 py-1.5" data-testid="bulk-anonymize"><ShieldOff size={12}/> Anonymisieren</button>
            <button onClick={() => bulk("mark_spam")} className="inline-flex items-center gap-1 border border-white/15 hover:bg-black/[0.15] text-xs font-bold rounded-lg px-3 py-1.5">Spam</button>
            <button onClick={() => bulk("mark_test")} className="inline-flex items-center gap-1 border border-white/15 hover:bg-black/[0.15] text-xs font-bold rounded-lg px-3 py-1.5">Test</button>
            <button onClick={() => bulk("delete")} className="inline-flex items-center gap-1 bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 text-red-200 text-xs font-bold rounded-lg px-3 py-1.5" data-testid="bulk-delete"><Trash2 size={12}/> Löschen</button>
            <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-[#C9C6B4] hover:text-[#F3F1E8]">Auswahl aufheben</button>
          </div>
        )}

        {err && <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-100 p-4 text-sm mb-4">{err}</div>}
        {loading && <div className="text-[#C9C6B4] text-sm">Lade Leads…</div>}
        {!loading && filtered.length === 0 && <div className="rounded-2xl border border-white/12 bg-[#4E5A3B]/60 p-8 text-center text-[#C9C6B4]">Keine Leads.</div>}

        {/* Leads Table */}
        {filtered.length > 0 && (
          <div className="glass-strong rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[36px_1.4fr_1.4fr_1fr_120px_100px_44px] gap-3 px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#A8A58F] border-b border-white/10">
              <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} data-testid="select-all" />
              <div>Name</div><div>Kontakt</div><div>Wohnort</div><div>Status</div><div>Datum</div><div></div>
            </div>
            {filtered.map(l => (
              <div key={l.id} className={`grid grid-cols-[36px_1.4fr_1.4fr_1fr_120px_100px_44px] gap-3 px-4 py-3 items-center border-b border-white/5 hover:bg-black/[0.1] transition-colors ${selected.has(l.id) ? 'bg-black/[0.15]' : ''}`}>
                <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)} data-testid={`select-${l.id}`} />
                <button onClick={() => setDetailLead(l)} className="text-left" data-testid={`open-${l.id}`}>
                  <div className="font-bold text-[#F3F1E8]">{l.first_name} {l.last_name}</div>
                  <div className="text-[11px] text-[#A8A58F]">{l.tags?.join(", ") || "—"}</div>
                </button>
                <div className="text-xs text-[#E5E2D0] truncate">
                  <div><Mail size={10} className="inline mr-1"/> {l.email}</div>
                  <div><Phone size={10} className="inline mr-1"/> {l.mobile}</div>
                </div>
                <div className="text-xs text-[#C9C6B4]">{l.city}</div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full inline-block text-center" style={{background: `${STATUS_COLORS[l.status]||'#E8E5D0'}20`, color: STATUS_COLORS[l.status]||'#E8E5D0', border: `1px solid ${STATUS_COLORS[l.status]||'#E8E5D0'}40`}}>
                  {STATUS_LABELS[l.status] || l.status}
                </span>
                <div className="text-[11px] text-[#8F8C7A]">{new Date(l.created_at).toLocaleDateString("de-DE")}</div>
                <button onClick={() => setDetailLead(l)} className="text-[#C9C6B4] hover:text-[#F3F1E8]"><ChevronDown size={16}/></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {detailLead && <LeadDetailModal lead={detailLead} onClose={() => setDetailLead(null)} api={api} onUpdate={fetchLeads} />}
      {showPwdModal && <PasswordModal onClose={() => setShowPwdModal(false)} api={api} onSuccess={logout} force={!!me?.must_change_password} />}
    </div>
  );
};

// === Lead Detail Modal ===
const LeadDetailModal = ({ lead, onClose, api, onUpdate }) => {
  const [detail, setDetail] = useState(lead);
  const [noteText, setNoteText] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = async () => { try { const r = await api.get(`/leads/${lead.id}`); setDetail(r.data); onUpdate(); } catch (e) { /* ignore */ } };

  const changeStatus = async (status) => {
    try { await api.patch(`/leads/${lead.id}`, { status }); toast.success("Status aktualisiert."); await refresh(); }
    catch { toast.error("Fehlgeschlagen."); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    setLoading(true);
    try { await api.post(`/leads/${lead.id}/notes`, { text: noteText }); setNoteText(""); toast.success("Notiz gespeichert."); await refresh(); }
    finally { setLoading(false); }
  };
  const addTask = async () => {
    if (!taskTitle.trim()) return;
    setLoading(true);
    try { await api.post(`/leads/${lead.id}/tasks`, { title: taskTitle }); setTaskTitle(""); toast.success("Aufgabe erstellt."); await refresh(); }
    finally { setLoading(false); }
  };
  const toggleTask = async (t) => { try { await api.patch(`/leads/${lead.id}/tasks/${t.id}`, { done: !t.done }); await refresh(); } catch (e) { /* ignore */ } };
  const del = async () => {
    if (!window.confirm(`Lead ${detail.first_name} ${detail.last_name} wirklich dauerhaft löschen?`)) return;
    if (!window.confirm("Sicher? Dieser Vorgang ist unwiderruflich.")) return;
    try { await api.delete(`/leads/${lead.id}`); toast.success("Lead gelöscht."); onUpdate(); onClose(); }
    catch { toast.error("Löschen fehlgeschlagen."); }
  };

  const waLink = `https://wa.me/${detail.mobile.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hallo ${detail.first_name}, vielen Dank für Deine Anfrage bei Tobias Nunner.`)}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#5C6B49] border border-white/15 rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="lead-detail-modal">
        <div className="sticky top-0 bg-[#5C6B49] border-b border-white/10 p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="font-display text-2xl font-black text-[#F3F1E8]">{detail.first_name} {detail.last_name}</h2>
            <div className="text-xs text-[#A8A58F] mt-1">Eingegangen: {new Date(detail.created_at).toLocaleString("de-DE")}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full border border-white/15 hover:bg-black/[0.15] flex items-center justify-center" data-testid="close-detail"><X size={16}/></button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-2">Kontakt</div>
            <div className="space-y-2 text-sm text-[#E5E2D0]">
              <div className="flex items-center gap-2"><Mail size={14} className="text-[#A8A58F]"/><a className="hover:text-[#E8E5D0]" href={`mailto:${detail.email}`}>{detail.email}</a></div>
              <div className="flex items-center gap-2"><Phone size={14} className="text-[#A8A58F]"/><a className="hover:text-[#E8E5D0]" href={`tel:${detail.mobile}`}>{detail.mobile}</a></div>
              {detail.landline && <div className="flex items-center gap-2 text-[#C9C6B4]"><Phone size={14}/> {detail.landline}</div>}
              <div className="flex items-center gap-2 text-[#C9C6B4]"><Circle size={14} className="text-[#A8A58F]"/> {detail.city}</div>
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-bold rounded-full px-3 py-1.5"><MessageCircle size={12}/> WhatsApp</a>
            </div>

            {detail.message && (
              <>
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mt-5 mb-2">Nachricht</div>
                <div className="text-sm text-[#E5E2D0] bg-black/20 rounded-lg p-3 border border-white/8">{detail.message}</div>
              </>
            )}

            <div className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mt-5 mb-2">Status</div>
            <select value={detail.status} onChange={e => changeStatus(e.target.value)} className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="detail-status">
              {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>

            <div className="mt-5 flex flex-wrap gap-2">
              <button onClick={() => api.post("/leads/bulk", { ids: [detail.id], action: "archive" }).then(() => { toast.success("Archiviert."); onUpdate(); onClose(); })} className="inline-flex items-center gap-1 border border-white/15 hover:bg-black/[0.15] text-xs font-bold rounded-lg px-3 py-1.5"><Archive size={12}/> Archivieren</button>
              <button onClick={() => api.post("/leads/bulk", { ids: [detail.id], action: "anonymize" }).then(() => { toast.success("Anonymisiert."); onUpdate(); onClose(); })} className="inline-flex items-center gap-1 border border-white/15 hover:bg-black/[0.15] text-xs font-bold rounded-lg px-3 py-1.5"><ShieldOff size={12}/> Anonymisieren</button>
              <button onClick={del} className="inline-flex items-center gap-1 bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 text-red-200 text-xs font-bold rounded-lg px-3 py-1.5" data-testid="delete-lead-btn"><Trash2 size={12}/> Löschen</button>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-2">Notizen</div>
            <div className="flex gap-2 mb-3">
              <input value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote()} placeholder="Neue Notiz…" className="flex-1 bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] placeholder:text-[#A8A58F]" data-testid="note-input" />
              <button onClick={addNote} disabled={loading} className="bg-[#E8E5D0] text-[#3A4230] font-bold rounded-lg px-3 py-2 text-xs" data-testid="add-note-btn"><Send size={14}/></button>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(detail.notes||[]).slice().reverse().map(n => (
                <div key={n.id} className="text-xs bg-black/20 rounded-lg p-2 border border-white/8">
                  <div className="text-[#E5E2D0]">{n.text}</div>
                  <div className="text-[10px] text-[#8F8C7A] mt-1">{new Date(n.at).toLocaleString("de-DE")} · {n.by}</div>
                </div>
              ))}
              {(!detail.notes || detail.notes.length === 0) && <div className="text-xs text-[#8F8C7A]">Noch keine Notizen.</div>}
            </div>

            <div className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mt-5 mb-2">Aufgaben</div>
            <div className="flex gap-2 mb-3">
              <input value={taskTitle} onChange={e => setTaskTitle(e.target.value)} onKeyDown={e => e.key === "Enter" && addTask()} placeholder="Aufgabe…" className="flex-1 bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="task-input" />
              <button onClick={addTask} disabled={loading} className="bg-[#E8E5D0] text-[#3A4230] font-bold rounded-lg px-3 py-2 text-xs" data-testid="add-task-btn"><Send size={14}/></button>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {(detail.tasks||[]).map(t => (
                <label key={t.id} className="flex items-start gap-2 text-xs bg-black/20 rounded-lg p-2 border border-white/8 cursor-pointer">
                  <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(t)} className="mt-0.5" />
                  <div className="flex-1">
                    <div className={t.done ? "line-through text-[#8F8C7A]" : "text-[#E5E2D0]"}>{t.title}</div>
                  </div>
                </label>
              ))}
              {(!detail.tasks || detail.tasks.length === 0) && <div className="text-xs text-[#8F8C7A]">Keine offenen Aufgaben.</div>}
            </div>

            <div className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mt-5 mb-2">Aktivität</div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {(detail.activities||[]).slice().reverse().map(a => (
                <div key={a.id} className="text-[11px] flex items-start gap-2">
                  <Clock size={10} className="text-[#8F8C7A] mt-0.5 shrink-0"/>
                  <div className="flex-1">
                    <div className="text-[#E5E2D0]">{a.text}</div>
                    <div className="text-[#8F8C7A]">{new Date(a.at).toLocaleString("de-DE")}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// === Password Change Modal ===
const PasswordModal = ({ onClose, api, onSuccess, force = false }) => {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("Passwort mind. 8 Zeichen."); return; }
    setLoading(true);
    try {
      await api.post("/admin/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Passwort geändert. Bitte neu einloggen.");
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={force ? undefined : onClose}>
      <form onSubmit={submit} className="bg-[#5C6B49] border border-white/15 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="password-modal">
        <h2 className="font-display text-xl font-black mb-2">Passwort ändern</h2>
        {force && (
          <p className="text-sm text-[#F4C572] mb-4" data-testid="force-pwd-note">
            Aus Sicherheitsgründen musst du dein temporäres Start-Passwort jetzt ändern, bevor du fortfährst.
          </p>
        )}
        <div className="space-y-3">
          <input type="password" placeholder="Aktuelles Passwort" value={oldPw} onChange={e => setOldPw(e.target.value)} className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="pwd-old" required />
          <input type="password" placeholder="Neues Passwort (min. 8 Zeichen)" value={newPw} onChange={e => setNewPw(e.target.value)} className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="pwd-new" required />
        </div>
        <div className="flex gap-2 mt-5">
          {!force && <button type="button" onClick={onClose} className="flex-1 border border-white/15 hover:bg-black/[0.15] text-sm font-bold rounded-lg px-4 py-2">Abbrechen</button>}
          <button type="submit" disabled={loading} className="flex-1 bg-[#E8E5D0] text-[#3A4230] text-sm font-bold rounded-lg px-4 py-2 disabled:opacity-50" data-testid="pwd-submit">{loading ? "…" : "Ändern"}</button>
        </div>
      </form>
    </div>
  );
};

export default AdminLeads;
